import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  TEMPO_CONDICOES,
  type DiarioDetalhe,
  type DiarioFoto,
  type DiarioItem,
  type TempoCondicao,
  type TipoItem,
} from "../../../shared/diario";
import { api } from "../api/cliente";
import { chavesDiario, useDiario, useUsuariosLista } from "../api/diarios";
import { useAuth } from "../auth/AuthProvider";
import { useAvisos } from "../components/Avisos";
import { Carregando, ErroCarregamento } from "../components/ui";

const TEMPO_EMOJI: Record<TempoCondicao, string> = { SOL: "☀️", NUBLADO: "☁️", CHUVA: "🌧️", "MUITA CHUVA": "⛈️" };

/** Dias entre duas datas (inclusivo, contando o dia inicial). "" se faltar alguma. */
function diasEntre(inicio: string | null, fim: string | null): string {
  if (!inicio || !fim) return "";
  const [ay, am, ad] = inicio.slice(0, 10).split("-").map(Number);
  const [by, bm, bd] = fim.slice(0, 10).split("-").map(Number);
  if (!ay || !by) return "";
  const dias = Math.floor((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000) + 1;
  return dias > 0 ? String(dias) : "";
}

/** Data final = data de início + prazo (dias), contando inclusive o dia inicial. */
function somarPrazo(inicio: string | null, prazoDias: string): string | null {
  const p = parseInt(prazoDias, 10);
  if (!inicio || !Number.isFinite(p) || p <= 0) return null;
  const [y, m, d] = inicio.slice(0, 10).split("-").map(Number);
  if (!y) return null;
  const dt = new Date(Date.UTC(y, m - 1, d) + (p - 1) * 86400000);
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${dt.getUTCFullYear()}-${mm}-${dd}`;
}

function fmtDataBr(iso: string | null): string {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : "";
}

function normaliza(s: string): string {
  return (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
}

async function comprimir(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(String(fr.result));
    fr.onerror = rej;
    fr.readAsDataURL(file);
  });
  return new Promise<string>((res, rej) => {
    const im = new Image();
    im.onload = () => {
      const max = 2000;
      let { width: w, height: h } = im;
      if (w > h && w > max) { h = (h * max) / w; w = max; } else if (h >= w && h > max) { w = (w * max) / h; h = max; }
      const cv = document.createElement("canvas");
      cv.width = w; cv.height = h;
      const ctx = cv.getContext("2d")!;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(im, 0, 0, w, h);
      res(cv.toDataURL("image/jpeg", 0.9));
    };
    im.onerror = rej;
    im.src = dataUrl;
  });
}

export function DiarioEditor() {
  const { id } = useParams();
  const diarioId = Number(id);
  const consulta = useDiario(diarioId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { avisar, avisarErro } = useAvisos();
  const { usuario } = useAuth();
  const usuarios = useUsuariosLista();

  const [d, setD] = useState<DiarioDetalhe | null>(null);
  const [assinandoFiscalDireto, setAssinandoFiscalDireto] = useState(false);
  const [novoEquip, setNovoEquip] = useState("");
  const [novoMao, setNovoMao] = useState("");
  const [novoEqfisc, setNovoEqfisc] = useState("");
  const [assinando, setAssinando] = useState<null | "resp" | "fisc">(null);
  const inputCamera = useRef<HTMLInputElement>(null);
  const inputArquivos = useRef<HTMLInputElement>(null);
  const inputOS = useRef<HTMLInputElement>(null);
  const [enviandoOS, setEnviandoOS] = useState(false);
  const [lendoOS, setLendoOS] = useState(false);
  const [fiscalNaoCadastrado, setFiscalNaoCadastrado] = useState("");

  useEffect(() => { if (consulta.data) setD(consulta.data); }, [consulta.data]);

  const salvar = useMutation({
    mutationFn: (dd: DiarioDetalhe) =>
      api<DiarioDetalhe>(`/api/diarios/${diarioId}`, {
        method: "PUT",
        body: {
          ...dd,
          itens: dd.itens,
          fotos: dd.fotos.map((f, i) => ({ id: f.id, legenda: f.legenda, ordem: i })),
        },
      }),
    onSuccess: (dd) => {
      setD(dd);
      queryClient.invalidateQueries({ queryKey: chavesDiario.lista });
      queryClient.setQueryData(chavesDiario.item(diarioId), dd);
    },
    onError: avisarErro,
  });

  if (consulta.isLoading || !d) return <Carregando />;
  if (consulta.error) return <ErroCarregamento erro={consulta.error} />;

  const set = (campo: keyof DiarioDetalhe, valor: unknown) => setD({ ...d, [campo]: valor });

  // Permissões: só o criador (ou admin) edita o conteúdo; o fiscal designado pode assinar.
  const ehAdmin = usuario?.papel === "admin";
  const ehCriador = !!usuario && usuario.id === d.criado_por;
  const podeEditarConteudo = ehCriador || ehAdmin;
  const ehFiscal = !!usuario && usuario.id === d.fiscal_id;

  // Nº e "dias corridos" = dias entre a data de início e a data do diário; prazo = início → final.
  const diasCorridos = diasEntre(d.data_inicio, d.data);
  const dataFinalCalc = somarPrazo(d.data_inicio, d.prazo_dias); // início + prazo
  const dComputado: DiarioDetalhe = { ...d, numero: diasCorridos, dias_corridos: diasCorridos, data_final: dataFinalCalc ?? d.data_final };

  const itensDe = (tipo: TipoItem) => d.itens.filter((it) => it.tipo === tipo);
  const removerItem = (item: DiarioItem) => setD({ ...d, itens: d.itens.filter((x) => x !== item) });
  const mudarQtd = (item: DiarioItem, delta: number) => {
    setD({
      ...d,
      itens: d.itens.map((it) => (it === item ? { ...it, quantidade: Math.max(0, it.quantidade + delta) } : it)),
    });
  };
  const adicionarItem = (tipo: TipoItem, nome: string) => {
    const limpo = nome.trim().toUpperCase();
    if (!limpo) return;
    setD({ ...d, itens: [...d.itens, { tipo, nome: limpo, quantidade: 1, ordem: d.itens.length }] });
  };

  async function aoEscolherFotos(ev: React.ChangeEvent<HTMLInputElement>) {
    const arquivos = Array.from(ev.target.files ?? []);
    ev.target.value = "";
    for (const arq of arquivos) {
      try {
        const dataUrl = await comprimir(arq);
        const foto = await api<DiarioFoto>(`/api/diarios/${diarioId}/fotos`, { method: "POST", body: { dataUrl, legenda: "" } });
        setD((prev) => (prev ? { ...prev, fotos: [...prev.fotos, foto] } : prev));
      } catch (e) {
        avisarErro(e);
      }
    }
  }

  async function enviarOS(ev: React.ChangeEvent<HTMLInputElement>) {
    const arq = ev.target.files?.[0];
    ev.target.value = "";
    if (!arq) return;
    if (arq.type !== "application/pdf") { avisarErro(new Error("Selecione um arquivo PDF.")); return; }
    setEnviandoOS(true);
    try {
      const dataUrl = await new Promise<string>((res, rej) => {
        const fr = new FileReader(); fr.onload = () => res(String(fr.result)); fr.onerror = rej; fr.readAsDataURL(arq);
      });
      const r = await api<{ caminho: string; url: string }>(`/api/diarios/${diarioId}/ordem-servico`, { method: "POST", body: { dataUrl } });
      setD((prev) => (prev ? { ...prev, ordem_servico: r.caminho, ordem_servico_url: r.url } : prev));
      avisar("Ordem de Serviço anexada.");
    } catch (e) { avisarErro(e); } finally { setEnviandoOS(false); }
  }
  async function removerOS() {
    try {
      await api(`/api/diarios/${diarioId}/ordem-servico`, { method: "DELETE" });
      setD((prev) => (prev ? { ...prev, ordem_servico: "", ordem_servico_url: undefined } : prev));
    } catch (e) { avisarErro(e); }
  }
  async function lerOS() {
    setLendoOS(true);
    try {
      const dados = await api<{ obra: string; empresa_contratada: string; numero_contrato: string; data_inicio: string | null; prazo_dias: string; fiscal_nome: string }>(
        `/api/diarios/${diarioId}/ler-ordem-servico`, { method: "POST" });
      const lista = usuarios.data ?? [];
      const alvo = normaliza(dados.fiscal_nome);
      const match = alvo
        ? (lista.find((u) => normaliza(u.nome) === alvo) ||
           lista.find((u) => { const n = normaliza(u.nome); return n.length > 3 && (n.includes(alvo) || alvo.includes(n)); }))
        : undefined;
      setFiscalNaoCadastrado(dados.fiscal_nome && !match ? dados.fiscal_nome : "");
      setD((prev) => prev ? {
        ...prev,
        obra: dados.obra || prev.obra,
        empresa_contratada: dados.empresa_contratada || prev.empresa_contratada,
        numero_contrato: dados.numero_contrato || prev.numero_contrato,
        data_inicio: dados.data_inicio ?? prev.data_inicio,
        prazo_dias: dados.prazo_dias || prev.prazo_dias,
        fiscal_id: match ? match.id : prev.fiscal_id,
      } : prev);
      avisar(match ? "Campos preenchidos e fiscal reconhecido. Confira antes de salvar." : "Campos preenchidos a partir da O.S. Confira antes de salvar.");
    } catch (e) { avisarErro(e); } finally { setLendoOS(false); }
  }

  const salvarEVoltar = () => salvar.mutate(dComputado, { onSuccess: () => navigate("/diarios") });
  const gerarPDF = () => salvar.mutate(dComputado, { onSuccess: () => navigate(`/diarios/${diarioId}/imprimir`) });

  return (
    <div className="diario-editor">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0 }}>Diário — {d.numero ? `${d.numero}º dia corrido` : "novo"}</h1>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn" onClick={() => (podeEditarConteudo ? salvarEVoltar() : navigate("/diarios"))} disabled={salvar.isPending}>
            {podeEditarConteudo ? "‹ Salvar e voltar" : "‹ Voltar"}
          </button>
          <button className="btn primary" onClick={() => (podeEditarConteudo ? gerarPDF() : navigate(`/diarios/${diarioId}/imprimir`))} disabled={salvar.isPending}>📄 Gerar PDF</button>
        </div>
      </div>

      {!podeEditarConteudo && (
        <div className="aviso-leitura">
          🔒 Somente leitura — apenas quem criou o diário edita as informações{d.criado_por_nome ? ` (${d.criado_por_nome})` : ""}.
          {ehFiscal && " Você é o fiscal designado: use “Assinar como fiscal”."}
        </div>
      )}
      {ehFiscal && !podeEditarConteudo && (
        <button className="btn primary" style={{ marginBottom: 10 }} onClick={() => setAssinandoFiscalDireto(true)}>✍️ Assinar como fiscal</button>
      )}

      <fieldset className="editor-fs" disabled={!podeEditarConteudo}>
      <section className="card">
        <h2>Identificação</h2>
        <div className="form-row">
          <label>Nº (dias corridos)<input type="text" value={diasCorridos} readOnly className="campo-auto" placeholder="—" title="Calculado: dias entre a data de início e a data do diário" /></label>
          <label>Data{ehAdmin ? " (montado na inserção)" : " (dia real — fixa)"}<input type="date" value={d.data} onChange={(e) => set("data", e.target.value)} disabled={!ehAdmin} className={ehAdmin ? undefined : "campo-auto"} /></label>
        </div>
      </section>

      <section className="card">
        <h2>Obra e Contrato</h2>
        <div className="os-campo">
          <span className="rot-forte">Ordem de Serviço (PDF)</span>
          {d.ordem_servico ? (
            <div className="os-anexado">
              <a className="btn" href={d.ordem_servico_url} target="_blank" rel="noopener noreferrer">📄 Ver O.S.</a>
              <button className="btn primary" onClick={lerOS} disabled={lendoOS}>{lendoOS ? "Lendo…" : "🤖 Ler e preencher"}</button>
              <button className="btn" onClick={() => inputOS.current?.click()} disabled={enviandoOS}>Trocar</button>
              <button className="btn danger" onClick={removerOS}>Remover</button>
            </div>
          ) : (
            <button className="btn primary" onClick={() => inputOS.current?.click()} disabled={enviandoOS}>
              {enviandoOS ? "Enviando…" : "📎 Adicionar O.S. (PDF)"}
            </button>
          )}
          <input ref={inputOS} type="file" accept="application/pdf" hidden onChange={enviarOS} />
        </div>
        <label>Obra<input type="text" value={d.obra} onChange={(e) => set("obra", e.target.value)} placeholder="Nome / descrição da obra" /></label>
        <div className="form-row">
          <label>Empresa contratada<input type="text" value={d.empresa_contratada} onChange={(e) => set("empresa_contratada", e.target.value)} /></label>
          <label>Nº do contrato<input type="text" value={d.numero_contrato} onChange={(e) => set("numero_contrato", e.target.value)} /></label>
        </div>
        <div className="form-row">
          <label>Data início<input type="date" value={d.data_inicio ?? ""} onChange={(e) => set("data_inicio", e.target.value || null)} /></label>
          <label>Prazo (dias)<input type="text" inputMode="numeric" value={d.prazo_dias} onChange={(e) => set("prazo_dias", e.target.value)} placeholder="ex.: 720" /></label>
          <label>Data final (calculada)<input type="text" value={fmtDataBr(dataFinalCalc)} readOnly className="campo-auto" placeholder="—" title="Calculada: data de início + prazo (dias)" /></label>
          <label>Dias corridos<input type="text" value={diasCorridos} readOnly className="campo-auto" placeholder="—" title="Calculado: dias entre a data de início e a data do diário" /></label>
        </div>
        <label>Fiscal do contrato (indique um usuário cadastrado)
          <select value={d.fiscal_id ?? ""} onChange={(e) => set("fiscal_id", e.target.value ? Number(e.target.value) : null)}>
            <option value="">— selecione o fiscal —</option>
            {(usuarios.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>{u.nome}</option>
            ))}
          </select>
        </label>
        {d.fiscal_id && d.fiscal_nome && <p className="note" style={{ marginTop: 4 }}>Fiscal designado: <strong>{d.fiscal_nome}</strong>. Ele(a) entra no sistema e assina como fiscal.</p>}
        {fiscalNaoCadastrado && (
          <p className="aviso-fiscal">⚠️ O fiscal indicado na O.S. — <strong>{fiscalNaoCadastrado}</strong> — ainda não está cadastrado no sistema. Peça que ele crie uma conta em diarios.wladmir.com e depois selecione-o aqui. Uma vez cadastrado, ele poderá acessar e assinar os diários.</p>
        )}
      </section>

      <section className="card">
        <h2>Serviços e Observações</h2>
        <div className="form-row caixas-simetricas">
          <label>Serviços executados<textarea className="caixa-grande" value={d.servicos_executados} onChange={(e) => set("servicos_executados", e.target.value)} /></label>
          <label>Observações a serem feitas pela fiscalização no serviço executado<textarea className="caixa-grande" value={d.observacoes_fiscalizacao} onChange={(e) => set("observacoes_fiscalizacao", e.target.value)} /></label>
        </div>
        <div className="form-row">
          <label>Observação (contratada)<textarea rows={2} value={d.observacao_contratada} onChange={(e) => set("observacao_contratada", e.target.value)} /></label>
          <label>Observação (fiscalização)<textarea rows={2} value={d.observacao_fiscalizacao_rodape} onChange={(e) => set("observacao_fiscalizacao_rodape", e.target.value)} /></label>
        </div>
      </section>

      <section className="card">
        <h2>Tempo</h2>
        {(["tempo_manha", "tempo_tarde"] as const).map((campo) => (
          <div key={campo} className="tempo-periodo">
            <span className="tempo-titulo">{campo === "tempo_manha" ? "Manhã" : "Tarde"}</span>
            <div className="tempo-chips">
              {TEMPO_CONDICOES.map((cond) => (
                <button
                  key={cond}
                  type="button"
                  className={`tempo-chip${d[campo] === cond ? " on" : ""}`}
                  onClick={() => set(campo, d[campo] === cond ? null : cond)}
                >
                  <span className="em">{TEMPO_EMOJI[cond]}</span>
                  <span>{cond}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </section>

      <ListaItens titulo="Equipamentos Utilizados" itens={itensDe("equipamento")} mudar={mudarQtd} remover={removerItem}
        novo={novoEquip} setNovo={setNovoEquip} adicionar={() => { adicionarItem("equipamento", novoEquip); setNovoEquip(""); }} placeholder="Adicionar equipamento" />
      <ListaItens titulo="Mão-de-Obra" itens={itensDe("mao_obra")} mudar={mudarQtd} remover={removerItem}
        novo={novoMao} setNovo={setNovoMao} adicionar={() => { adicionarItem("mao_obra", novoMao); setNovoMao(""); }} placeholder="Adicionar função" />
      <ListaItens titulo="Equipe da Fiscalização" itens={itensDe("equipe_fiscalizacao")} mudar={mudarQtd} remover={removerItem}
        novo={novoEqfisc} setNovo={setNovoEqfisc} adicionar={() => { adicionarItem("equipe_fiscalizacao", novoEqfisc); setNovoEqfisc(""); }} placeholder="Adicionar função" />

      <section className="card">
        <h2>Fotos</h2>
        <div className="foto-botoes">
          <button className="btn primary" onClick={() => inputCamera.current?.click()}>📷 Tirar foto</button>
          <button className="btn" onClick={() => inputArquivos.current?.click()}>🖼️ Galeria / Arquivos</button>
        </div>
        <input ref={inputCamera} type="file" accept="image/*" capture="environment" hidden onChange={aoEscolherFotos} />
        <input ref={inputArquivos} type="file" accept="image/*" multiple hidden onChange={aoEscolherFotos} />
        {d.fotos.length === 0 ? (
          <p className="note" style={{ marginTop: 10 }}>Nenhuma foto adicionada.</p>
        ) : (
          <div className="diario-fotos">
            {d.fotos.map((f, i) => (
              <div key={f.id ?? i} className="diario-foto">
                {f.url && <img src={f.url} alt={f.legenda} />}
                <input className="foto-legenda" type="text" placeholder="Escreva a legenda desta foto" value={f.legenda}
                  onChange={(e) => setD({ ...d, fotos: d.fotos.map((x, j) => (j === i ? { ...x, legenda: e.target.value } : x)) })} />
                <button className="btn danger foto-remover" onClick={() => setD({ ...d, fotos: d.fotos.filter((_, j) => j !== i) })}>Remover foto</button>
              </div>
            ))}
          </div>
        )}
        <p className="note" style={{ marginTop: 8 }}>As fotos são enviadas na hora; a remoção é efetivada ao salvar.</p>
      </section>

      <section className="card">
        <h2>Assinaturas</h2>
        <BotaoAssinatura titulo="Responsável (Engenheiro da obra)" nome={d.assinatura_responsavel_nome} img={d.assinatura_responsavel_img} onClick={() => setAssinando("resp")} />
        <BotaoAssinatura titulo="Fiscal do contrato" nome={d.assinatura_fiscal_nome} img={d.assinatura_fiscal_img} onClick={() => setAssinando("fisc")} />
        <a className="btn gov" href="https://assinador.iti.br/" target="_blank" rel="noopener noreferrer">🔏 Assinatura digital gov.br</a>
        <p className="note" style={{ marginTop: 6 }}>
          Para assinatura digital oficial: gere o PDF, salve o arquivo e assine no portal gov.br (é preciso conta gov.br nível prata ou ouro).
        </p>
      </section>

      </fieldset>

      {podeEditarConteudo && (
        <div className="diario-toolbar">
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={salvarEVoltar} disabled={salvar.isPending}>{salvar.isPending ? "Salvando…" : "Salvar e voltar"}</button>
          <button className="btn primary" onClick={gerarPDF} disabled={salvar.isPending}>📄 Gerar PDF</button>
        </div>
      )}

      {assinando && (
        <PadAssinatura
          titulo={assinando === "resp" ? "Responsável (Engenheiro)" : "Fiscal do Contrato"}
          nomeInicial={assinando === "resp" ? d.assinatura_responsavel_nome : d.assinatura_fiscal_nome}
          imgInicial={assinando === "resp" ? d.assinatura_responsavel_img : d.assinatura_fiscal_img}
          onFechar={() => setAssinando(null)}
          onSalvar={(nome, img) => {
            if (assinando === "resp") setD({ ...d, assinatura_responsavel_nome: nome, assinatura_responsavel_img: img });
            else setD({ ...d, assinatura_fiscal_nome: nome, assinatura_fiscal_img: img });
            setAssinando(null);
            avisar("Assinatura registrada. Não esqueça de salvar.");
          }}
        />
      )}

      {assinandoFiscalDireto && (
        <PadAssinatura
          titulo="Assinar como Fiscal do Contrato"
          nomeInicial={d.assinatura_fiscal_nome || usuario?.nome || ""}
          imgInicial={d.assinatura_fiscal_img}
          onFechar={() => setAssinandoFiscalDireto(false)}
          onSalvar={async (nome, img) => {
            try {
              const atualizado = await api<DiarioDetalhe>(`/api/diarios/${diarioId}/assinar-fiscal`, { method: "POST", body: { nome, img } });
              setD(atualizado);
              queryClient.invalidateQueries({ queryKey: chavesDiario.lista });
              avisar("Assinatura do fiscal registrada.");
            } catch (e) {
              avisarErro(e);
            }
            setAssinandoFiscalDireto(false);
          }}
        />
      )}
    </div>
  );
}

function ListaItens(props: {
  titulo: string;
  itens: DiarioItem[];
  mudar: (item: DiarioItem, delta: number) => void;
  remover?: (item: DiarioItem) => void;
  novo?: string;
  setNovo?: (v: string) => void;
  adicionar?: () => void;
  placeholder?: string;
}) {
  return (
    <section className="card">
      <h2>{props.titulo}</h2>
      <div className="diario-itens">
        {props.itens.map((it, i) => (
          <div key={i} className={`diario-item${it.quantidade > 0 ? " ativo" : ""}`}>
            <span className="nome">{it.nome}</span>
            <span className="qtd">
              <button type="button" onClick={() => props.mudar(it, -1)}>–</button>
              <span className="v">{it.quantidade}</span>
              <button type="button" onClick={() => props.mudar(it, +1)}>+</button>
            </span>
            {props.remover && (
              <button type="button" className="item-remover" title="Remover" onClick={() => props.remover?.(it)}>🗑</button>
            )}
          </div>
        ))}
      </div>
      {props.adicionar && (
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <input type="text" placeholder={props.placeholder} value={props.novo}
            onChange={(e) => props.setNovo?.(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); props.adicionar?.(); } }} />
          <button className="btn primary" type="button" onClick={props.adicionar}>+</button>
        </div>
      )}
    </section>
  );
}

function BotaoAssinatura({ titulo, nome, img, onClick }: { titulo: string; nome: string; img: string | null; onClick: () => void }) {
  return (
    <button type="button" className="diario-ass" onClick={onClick}>
      <span style={{ flex: 1, textAlign: "left" }}>
        <strong>{titulo}</strong>
        <span className="note" style={{ display: "block" }}>{nome || "Toque para assinar"}</span>
      </span>
      {img ? <img src={img} alt="assinatura" /> : <span className="pen">✎</span>}
    </button>
  );
}

function PadAssinatura(props: {
  titulo: string;
  nomeInicial: string;
  imgInicial: string | null;
  onFechar: () => void;
  onSalvar: (nome: string, img: string | null) => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [nome, setNome] = useState(props.nomeInicial);
  const desenhando = useRef(false);
  const vazio = useRef(!props.imgInicial);

  useEffect(() => {
    const cv = canvas.current!;
    const r = cv.getBoundingClientRect();
    cv.width = r.width * 2; cv.height = 230 * 2;
    const ctx = cv.getContext("2d")!;
    ctx.scale(2, 2);
    ctx.lineWidth = 2.4; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#0a0f1a";
    if (props.imgInicial) { const im = new Image(); im.onload = () => ctx.drawImage(im, 0, 0, r.width, 230); im.src = props.imgInicial; }
  }, [props.imgInicial]);

  const pos = (ev: React.PointerEvent) => {
    const b = canvas.current!.getBoundingClientRect();
    return { x: ev.clientX - b.left, y: ev.clientY - b.top };
  };

  function recortar(): string | null {
    const cv = canvas.current!;
    const ctx = cv.getContext("2d")!;
    const { width: W, height: H } = cv;
    const dt = ctx.getImageData(0, 0, W, H).data;
    let minX = W, minY = H, maxX = 0, maxY = 0, achou = false;
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      if (dt[(y * W + x) * 4 + 3] > 10) { achou = true; if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y; }
    }
    if (!achou) return null;
    const pad = 12;
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad); maxX = Math.min(W, maxX + pad); maxY = Math.min(H, maxY + pad);
    const w = maxX - minX, h = maxY - minY;
    const o = document.createElement("canvas"); o.width = w; o.height = h;
    o.getContext("2d")!.drawImage(cv, minX, minY, w, h, 0, 0, w, h);
    return o.toDataURL("image/png");
  }

  return (
    <div className="diario-overlay" onClick={props.onFechar}>
      <div className="diario-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{props.titulo}</h2>
        <label>Nome de quem assina<input type="text" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome" /></label>
        <div className="note" style={{ margin: "10px 0 6px" }}>Assine no quadro</div>
        <div className="pad-wrap">
          <canvas
            ref={canvas}
            className="pad-canvas"
            onPointerDown={(e) => { e.preventDefault(); desenhando.current = true; vazio.current = false; const ctx = canvas.current!.getContext("2d")!; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); }}
            onPointerMove={(e) => { if (!desenhando.current) return; const ctx = canvas.current!.getContext("2d")!; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); }}
            onPointerUp={() => { desenhando.current = false; }}
            onPointerLeave={() => { desenhando.current = false; }}
          />
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          <button className="btn" onClick={() => { const cv = canvas.current!; cv.getContext("2d")!.clearRect(0, 0, cv.width, cv.height); vazio.current = true; }}>Limpar</button>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={props.onFechar}>Cancelar</button>
          <button className="btn primary" onClick={() => props.onSalvar(nome.trim(), vazio.current ? props.imgInicial : recortar())}>Salvar</button>
        </div>
      </div>
    </div>
  );
}
